import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Header.css";

function Header() {
  const [logoUrl, setLogoUrl] = useState("");
  const [textPages, setTextPages] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const [contactSettings, setContactSettings] = useState(null);
  const [allProducts, setAllProducts] = useState([]); // Store all products for search

  const navigate = useNavigate();

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}api/logos/`);

        const data = await response.json();
        if (data.length > 0) {
          const logo = data[0].image;
          setLogoUrl(logo);
    
          // ✅ Dynamically set favicon
          const link = document.querySelector("link[rel*='icon']") || document.createElement("link");
          link.type = "image/png";
          link.rel = "shortcut icon";
          link.href = logo;
          document.getElementsByTagName("head")[0].appendChild(link);
        }
      } catch (error) {
        console.error("Error fetching logo:", error);
      }
    };

    const fetchAudioSetting = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}api/audio-settings/`);

        const data = await response.json();
        if (data && data.is_enabled === true) {
          setIsAudioEnabled(true);
        }
      } catch (error) {
        console.error("Error fetching audio settings:", error);
      }
    };

    const fetchContactSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}api/contact-settings/`);

        const data = await response.json();
        if (data.length > 0) {
          setContactSettings(data[0]);
        }
      } catch (error) {
        console.error("Error fetching contact settings:", error);
      }
    };

    const fetchTextPages = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}api/text-pages/`);

        const data = await response.json();
        setTextPages(data);
      } catch (error) {
        console.error("Error fetching text pages:", error);
      }
    };

    // Fetch all products for search functionality
    const fetchAllProducts = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}api/products/`);



        const data = await response.json();
        setAllProducts(data);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchLogo();
    fetchAudioSetting();
    fetchContactSettings();
    fetchTextPages();
    fetchAllProducts();
  }, []);

  // ✅ Speak Function
  const speakText = (text) => {
    if (!text || !isAudioEnabled) return;

    speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "en-IN"; // or en-US
    speechSynthesis.speak(utterance);
  };

  // Search functionality
  const performSearch = (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const searchTerm = query.toLowerCase();
    const results = [];

    // Search through all products
    allProducts.forEach(product => {
      const categoryMatch = product.category.toLowerCase().includes(searchTerm);
      const descriptionMatch = product.description && product.description.toLowerCase().includes(searchTerm);
      const brandMatch = product.brand_name && product.brand_name.toLowerCase().includes(searchTerm);

      if (categoryMatch || descriptionMatch || brandMatch) {
        // Find parent and children information
        const parent = allProducts.find(p => p.id === product.parent);
        const children = allProducts.filter(p => p.parent === product.id);

        results.push({
          ...product,
          parentInfo: parent,
          childrenInfo: children,
          matchType: categoryMatch ? 'category' : descriptionMatch ? 'description' : 'brand'
        });
      }
    });

    setSearchResults(results);
    setShowSearchResults(true);
  };

  const handleSearch = () => {
    performSearch(searchQuery);
    speakText(`Searching for ${searchQuery}`);
  };

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Perform real-time search as user types
    if (query.length > 2) {
      performSearch(query);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  const handleSearchResultClick = (result) => {
    // Navigate to products page and pass the selected category info
    navigate("/products", { 
      state: { 
        selectedCategoryId: result.id,
        searchResult: result 
      } 
    });
    
    speakText(`Opening ${result.category}`);
    setShowSearch(false);
    setShowSearchResults(false);
    setSearchQuery("");
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setShowSearchResults(false);
  };

  const handleWhatsAppClick = () => {
    if (contactSettings?.whatsapp_number) {
      const whatsappUrl = `https://wa.me/${contactSettings.whatsapp_number}`;
      window.open(whatsappUrl, '_blank');
      speakText("Opening WhatsApp");
    }
  };

  const handleEmailClick = () => {
    if (contactSettings?.email) {
      const emailUrl = `mailto:${contactSettings.email}`;
      window.open(emailUrl, '_blank');
      speakText("Opening email");
    }
  };

  const handleProductClick = () => {
    speechSynthesis.cancel();

    setTimeout(() => {
      speakText("Viewing our product catalog");
    }, 100);

    setTimeout(() => {
      navigate("/products");
    }, 500);
  };

  const handleHomeClick = () => {
    speechSynthesis.cancel();

    setTimeout(() => {
      speakText("Welcome to the homepage");
    }, 100);

    setTimeout(() => {
      navigate("/");
    }, 500);
  };

  const handleBrandsClick = () => {
    speechSynthesis.cancel();

    setTimeout(() => {
      speakText("Here are our brands");
      navigate("/brands");
    }, 100);
  };

  const handleTextPageClick = (page) => {
    speechSynthesis.cancel();

    setTimeout(() => {
      speakText(`Opening ${page.name}`);
    }, 100);

    setTimeout(() => {
      navigate(`/${page.name.replace(/\s+/g, "-").toLowerCase()}`);
    }, 500);
  };

  return (
    <header className="header">
      {/* Logo Section - 20% */}
      <div className="logo-section">
        <span className="logo-link" onClick={handleHomeClick}>
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="logo-image" />
          ) : (
            <div className="logo-placeholder">MyApp</div>
          )}
        </span>
      </div>

      {/* Navigation Section - 80% */}
      <div className="nav-section">
        {/* Top Row - Contact Details */}
        <div className="contact-row">
          {contactSettings && (
            <>
              {contactSettings.email && (
                <div
                  className="contact-item email-contact"
                  onClick={handleEmailClick}
                  title="Send Email"
                >
                  <div className="contact-icon email-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                    </svg>
                  </div>
                  <span className="contact-text">{contactSettings.email}</span>
                </div>
              )}
              {contactSettings.whatsapp_number && (
                <div
                  className="contact-item whatsapp-contact"
                  onClick={handleWhatsAppClick}
                  title="Contact via WhatsApp"
                >
                  <div className="contact-icon whatsapp-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.891 3.488"/>
                    </svg>
                  </div>
                  <span className="contact-text">+{contactSettings.whatsapp_number}</span>
                </div>
              )}
            </>
          )}
          
          {/* Search Button */}
          <button
            className="search-toggle"
            onClick={() => setShowSearch((prev) => !prev)}
            aria-label="Toggle search input"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
          </button>
        </div>

        {/* Bottom Row - Navigation Links */}
        <div className="nav-row">
          <nav className="nav-links">
            <span className="nav-item" onClick={handleProductClick}>
              Products
            </span>

            {textPages.map((page) => (
              <span
                key={page.id}
                className="nav-item"
                onClick={() => handleTextPageClick(page)}
              >
                {page.name}
              </span>
            ))}

            <span className="nav-item" onClick={handleBrandsClick}>
              Brands
            </span>
          </nav>
        </div>

        {/* Search Box */}
        <div className={`search-box ${showSearch ? "show" : ""}`}>
          <input
            type="text"
            placeholder="Search products, categories, brands..."
            value={searchQuery}
            onChange={handleSearchInputChange}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button onClick={handleSearch} className="search-submit">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
          </button>
          {searchQuery && (
            <button onClick={clearSearch} className="search-clear">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          )}
        </div>

        {/* Search Results Dropdown */}
        {showSearchResults && searchResults.length > 0 && (
          <div className="search-results">
            <div className="search-results-header">
              <h4>Search Results ({searchResults.length})</h4>
            </div>
            <div className="search-results-list">
              {searchResults.map((result) => (
                <div
                  key={result.id}
                  className="search-result-item"
                  onClick={() => handleSearchResultClick(result)}
                >
                  <div className="search-result-main">
                    <img 
                      src={result.image} 
                      alt={result.category} 
                      className="search-result-image"
                    />
                    <div className="search-result-content">
                      <h5>{result.category}</h5>
                      {result.brand_name && (
                        <p className="search-result-brand">Brand: {result.brand_name}</p>
                      )}
                      {result.description && (
                        <p className="search-result-description">
                          {result.description.substring(0, 100)}...
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Show parent/child relationships */}
                  <div className="search-result-hierarchy">
                    {result.parentInfo && (
                      <div className="parent-info">
                        <span className="hierarchy-label">Parent:</span>
                        <span className="hierarchy-name">{result.parentInfo.category}</span>
                      </div>
                    )}
                    {result.childrenInfo.length > 0 && (
                      <div className="children-info">
                        <span className="hierarchy-label">Children:</span>
                        <span className="hierarchy-name">
                          {result.childrenInfo.map(child => child.category).join(", ")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {showSearchResults && searchResults.length === 0 && searchQuery.length > 2 && (
          <div className="search-results">
            <div className="no-results">
              <p>No results found for "{searchQuery}"</p>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;